import { useState } from 'react'

import {
  FinancialWorkspaceUpdateSettingDocument,
  GetFinancialWorkspaceSettingsDocument,
} from 'generated/resources/graphql'

import { useForm } from 'modules/core/hooks/useForm'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import {
  FinancialWorkspaceSettingsFormValues,
  LABOR_COST_CONCEPT_CALCULATION_TYPES,
  LABOR_COST_TYPES,
} from './constants'

const getConceptIdsByCalculationType = (
  laborCostConcepts: Array<{ conceptId: number; laborCostCalculationType: string }> | undefined,
  calculationType: string
) => {
  return (
    laborCostConcepts
      ?.filter((concept) => concept.laborCostCalculationType === calculationType)
      .map((concept) => Number(concept.conceptId)) || []
  )
}

export const useFinancialWorkspaceSettings = () => {
  const [expensesConceptDetected, setExpensesConceptDetected] = useState<boolean>(false)

  const {
    data: financialWorkspaceSettings,
    loading: financialWorkspaceSettingsLoading,
    refetch: refetchFinancialWorkspaceSettings,
  } = useQuery(GetFinancialWorkspaceSettingsDocument)

  const currentFinancialWorkspaceSettings =
    financialWorkspaceSettings?.financialWorkspace?.settingsConnection?.edges[0]?.node

  const [updateFinancialWorkspaceSetting, { loading: updateLoading }] = useMutation(
    FinancialWorkspaceUpdateSettingDocument,
    { extractorFn: (data) => data.financialWorkspaceMutations.updateFinancialworkspaceSetting }
  )

  const defaultConceptIdsToAdd = getConceptIdsByCalculationType(
    currentFinancialWorkspaceSettings?.laborCostConcepts,
    LABOR_COST_CONCEPT_CALCULATION_TYPES.ADD_TO_TOTAL
  )

  const defaultConceptIdsToSubtract = getConceptIdsByCalculationType(
    currentFinancialWorkspaceSettings?.laborCostConcepts,
    LABOR_COST_CONCEPT_CALCULATION_TYPES.SUBTRACT_FROM_TOTAL
  )

  const { values, setFieldValue, dirty, handleSubmit, resetForm } =
    useForm<FinancialWorkspaceSettingsFormValues>({
      defaultValues: {
        laborCostType:
          currentFinancialWorkspaceSettings?.laborCostType || LABOR_COST_TYPES.COMPANY_COST,
        conceptIdsToAdd: defaultConceptIdsToAdd,
        conceptIdsToSubtract: defaultConceptIdsToSubtract,
        subtractEmployeeExpenses:
          currentFinancialWorkspaceSettings?.subtractEmployeeExpenses || false,
      },
      async onSubmit(data, { setErrors }) {
        const laborCostConceptsToAdd = data.conceptIdsToAdd.map((conceptId) => ({
          conceptId,
          laborCostCalculationType: LABOR_COST_CONCEPT_CALCULATION_TYPES.ADD_TO_TOTAL,
        }))

        const laborCostConceptsToSubtract = data.conceptIdsToSubtract.map((conceptId) => ({
          conceptId,
          laborCostCalculationType: LABOR_COST_CONCEPT_CALCULATION_TYPES.SUBTRACT_FROM_TOTAL,
        }))

        const result = await updateFinancialWorkspaceSetting({
          variables: {
            id: Number(currentFinancialWorkspaceSettings?.id),
            laborCostType: data.laborCostType,
            laborCostConcepts: [...laborCostConceptsToAdd, ...laborCostConceptsToSubtract],
            subtractEmployeeExpenses:
              data.laborCostType === LABOR_COST_TYPES.SIMPLE_CALCULATION
                ? false
                : data.subtractEmployeeExpenses,
          },
        })

        const handled = handleResult(result, (resultErrors) => {
          setErrors(resultErrors)
        })

        if (handled.success) {
          refetchFinancialWorkspaceSettings()
          app.setFlash({
            type: 'correct',
            message: i18n.t('common.saved_successfully'),
          })

          resetForm({ values })
        }

        return handled
      },
    })

  const showBanner = expensesConceptDetected && dirty
  const showSubtractFromTotal = values.laborCostType === LABOR_COST_TYPES.COMPANY_COST
  const showSubtractEmployeeExpensesSection =
    values.laborCostType === LABOR_COST_TYPES.SIMPLE_CALCULATION || values.subtractEmployeeExpenses

  const loading = updateLoading || financialWorkspaceSettingsLoading

  return {
    values,
    setFieldValue,
    dirty,
    handleSubmit,
    loading,
    showBanner,
    showSubtractFromTotal,
    showSubtractEmployeeExpensesSection,
    defaultConceptIdsToAdd,
    defaultConceptIdsToSubtract,
    expensesConceptDetected,
    setExpensesConceptDetected,
  }
}
